<template>
  <v-card>
    <v-card-text>
      <v-form
          ref="ru_form"
          v-model="validationRules.valid"
          lazy-validation
      >
        <v-row>
          <v-col cols="12" sm="12">
            <v-text-field
                dense
                outlined
                readonly
                :value="displayReservationPrice"
                label="Reservation Price"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
                dense
                outlined
                :rules="validationRules.amountRules"
                v-model="editedItem.returned_amount"
                maxLength="20"
                @keyup="editedItem.returned_amount = addTS($event); onReleaseAmountInput()"
                @keypress="isWholeNumber($event)"
                label="Amount Returned *"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
                dense
                outlined
                :rules="validationRules.percentageRules"
                v-model="editedItem.returned_percentage"
                maxLength="10"
                @input="onReleasePercentInput"
                @keypress="isWholeNumber($event)"
                label="Return (%) *"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="12">
            <v-textarea
                dense
                outlined
                :rules="validationRules.reasonRules"
                v-model="editedItem.release_reason"
                maxLength="300"
                counter="300"
                label="Reason *"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            :disabled="!validationRules.valid"
            color="blue darken-1"
            text
            @click="saveItem(editedItem)"
        >
          Save
        </v-btn>
      </v-card-actions>
      <v-col cols="12">
        <div v-if="errors!=undefined && errors.length >= 0"
             role="alert"
             v-bind:class="{ show: errors.length }"
             class="alert fade alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script>
import {mapState} from "vuex";
import {CLEAR_LISTINGS_ERRORS, RESERVE_UNIT} from "@/core/services/store/listings.module";

export default {
  name: 'release-unit',
  props: ['item'],
  data() {
    return {
      editedItem: {},
      displayReservationPrice: '',
      syncingReturnPricing: false,
      validationRules: {
        valid: true,
        amountRules: [
          v => !!v || "This field is required",
          v => (v && this.stripNumeric(v).length >= 1) || "Must be greater than 0",
          v => !this.reservationPriceRaw || parseFloat(this.stripNumeric(v)) <= this.reservationPriceRaw || "Cannot exceed reservation price"
        ],
        percentageRules: [
          v => !!v || "This field is required",
          v => (v && parseFloat(this.stripNumeric(v)) >= 0) || "Must be 0 or greater",
          v => (v && parseFloat(this.stripNumeric(v)) <= 100) || "Must be less than or equals to 100"
        ],
        reasonRules: [
          v => !!v || "This field is required",
          v => (v && v.length >= 3) || "Must be greater than 3 characters",
          v => (v && v.length <= 300) || "Must be less than 300 characters"
        ],
      },
    }
  },
  computed: {
    ...mapState({
      errors: state => state.listings.errors
    }),
    reservationPriceRaw() {
      if (this.displayReservationPrice && this.displayReservationPrice !== 'N/A') {
        return this.parseFormattedNumber(this.displayReservationPrice);
      }

      const candidates = [
        this.item.reservation_price,
        this.item.latest_reserve ? this.item.latest_reserve.price : null,
      ];

      for (const candidate of candidates) {
        const amount = this.parseFormattedNumber(candidate);
        if (amount > 0) {
          return amount;
        }
      }

      return 0;
    },
  },
  methods: {
    parseFormattedNumber(value) {
      if (value === null || value === undefined || value === '') {
        return 0;
      }
      if (typeof value === 'number') {
        return isNaN(value) ? 0 : value;
      }
      const normalized = String(value).replace(/[^0-9.]/g, '');
      const parsed = parseFloat(normalized);
      return isNaN(parsed) ? 0 : parsed;
    },
    stripNumeric(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).replace(/,/g, '');
    },
    formatReturnAmount(value) {
      const rounded = Math.round(this.parseFormattedNumber(value));
      return rounded > 0 ? this.addTSBV(String(rounded)) : '';
    },
    formatReturnPercentage(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(Math.round(this.parseFormattedNumber(value)));
    },
    isWholeNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        return evt.preventDefault();
      }
      return true;
    },
    onReleaseAmountInput() {
      if (this.syncingReturnPricing || !this.reservationPriceRaw) {
        return;
      }
      this.$nextTick(() => {
        const amount = this.parseFormattedNumber(this.editedItem.returned_amount);
        if (isNaN(amount)) {
          return;
        }
        this.syncingReturnPricing = true;
        this.editedItem.returned_amount = this.formatReturnAmount(amount);
        this.editedItem.returned_percentage = this.formatReturnPercentage((amount / this.reservationPriceRaw) * 100);
        this.syncingReturnPricing = false;
      });
    },
    onReleasePercentInput(inputValue) {
      if (this.syncingReturnPricing || !this.reservationPriceRaw) {
        return;
      }
      this.$nextTick(() => {
        const percentageSource = inputValue !== undefined && inputValue !== null
            ? inputValue
            : this.editedItem.returned_percentage;
        const percentage = this.parseFormattedNumber(percentageSource);
        if (isNaN(percentage)) {
          return;
        }
        this.syncingReturnPricing = true;
        this.editedItem.returned_percentage = this.formatReturnPercentage(percentage);
        this.editedItem.returned_amount = this.formatReturnAmount((percentage / 100) * this.reservationPriceRaw);
        this.syncingReturnPricing = false;
      });
    },
    resetReleaseForm() {
      this.editedItem = {};
      this.displayReservationPrice = '';
      this.syncingReturnPricing = false;
      this.$nextTick(() => {
        if (this.$refs.ru_form) {
          this.$refs.ru_form.reset();
          this.$refs.ru_form.resetValidation();
        }
      });
    },
    applyReservationPriceFromItem() {
      const candidates = [
        this.item.reservation_price,
        this.item.latest_reserve ? this.item.latest_reserve.price : null,
      ];

      for (const candidate of candidates) {
        const amount = this.parseFormattedNumber(candidate);
        if (amount > 0) {
          this.displayReservationPrice = this.addTSBV(String(Math.round(amount)));
          return;
        }
      }

      this.displayReservationPrice = 'N/A';
    },
    initializeReleaseForm() {
      this.resetReleaseForm();
      this.$nextTick(() => {
        this.applyReservationPriceFromItem();
      });
    },
    saveItem(ritem) {
      if (this.$refs.ru_form.validate()) {
        const payload = {
          type: 2,
          release_reason: ritem.release_reason,
          returned_amount: ritem.returned_amount
              ? String(Math.round(this.parseFormattedNumber(ritem.returned_amount)))
              : '',
          returned_percentage: ritem.returned_percentage
              ? String(Math.round(this.parseFormattedNumber(ritem.returned_percentage)))
              : '',
        };
        this.$store.dispatch(CLEAR_LISTINGS_ERRORS);
        this.$store.dispatch(RESERVE_UNIT, {'slug': this.item.id, data: payload}).then(() => {
          this.resetReleaseForm();
          this.$emit('relDClose')
        })
      }
    }
  },
  mounted() {
    this.initializeReleaseForm();
  },
  watch: {
    'item.id'() {
      this.initializeReleaseForm();
    },
    'item.reservation_price'() {
      this.applyReservationPriceFromItem();
    },
    'item.latest_reserve': {
      deep: true,
      handler() {
        this.applyReservationPriceFromItem();
      },
    },
  },
}
</script>
